<template>
  <section class="media">
    <div class="media__inner">

      <!-- Header -->
      <div class="media__header">
        <div>
          <h2 class="media__title">{{ $t('home.mediaTitle') }}</h2>
        </div>
        <div class="media__tabs">
          <button :class="{ active: tab === 'foto' }"  @click="tab = 'foto'">{{ $t('home.mediaPhotos') }}</button>
          <button :class="{ active: tab === 'video' }" @click="tab = 'video'">{{ $t('home.mediaVideos') }}</button>
        </div>
      </div>

      <transition name="media-tab" mode="out-in">
      <!-- ── Foto ─────────────────────────────────────────────── -->
      <div v-if="tab === 'foto'" key="foto">
        <!-- Row 1: 3 large cards (463×280) -->
        <div class="media__row media__row--3">
          <article class="media__card media__card--clickable" v-for="item in rowOne" :key="item.idx" @click="openPhoto(item)">
            <div class="media__img-wrap">
              <img :src="item.src" :alt="item.title" class="media__img" />
              <ViewCount :count="item.views" overlay />
            </div>
            <p class="media__card-title">{{ item.title }}</p>
          </article>
        </div>

        <!-- Row 2: 4 smaller cards (341×280) -->
        <div class="media__row media__row--4">
          <article class="media__card media__card--clickable" v-for="item in rowTwo" :key="item.idx" @click="openPhoto(item)">
            <div class="media__img-wrap">
              <img :src="item.src" :alt="item.title" class="media__img" />
              <ViewCount :count="item.views" overlay />
            </div>
            <p class="media__card-title">{{ item.title }}</p>
          </article>
        </div>

        <p v-if="!photos.length" class="media__subtitle">{{ $t('home.mediaNoPhotos') }}</p>
      </div>

      <!-- ── Video: 4×2 grid, the rest behind the pager ───────── -->
      <div v-else key="video">
        <div class="media__grid media__grid--video">
          <article
            class="media__card media__card--clickable"
            v-for="item in pagedVideos"
            :key="'v-' + item.id"
            @click="openVideo(item)"
          >
            <div class="media__img-wrap">
              <img v-if="posterFor(item)" :src="posterFor(item)" :alt="item.title" class="media__video" />
              <!-- no admin poster (uploaded clip) → show the clip's first frame -->
              <video
                v-else
                class="media__video"
                :src="`${src(item.url)}#t=0.1`"
                preload="metadata"
                muted
                playsinline
              ></video>
              <span class="media__play" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5v14l11-7L8 5z" />
                </svg>
              </span>
              <ViewCount :count="item.views || 0" overlay />
            </div>
            <p class="media__card-title">{{ item.title }}</p>
          </article>
        </div>
        <p v-if="!videos.length" class="media__subtitle">{{ $t('home.mediaNoVideos') }}</p>
        <AppPagination v-model="videoPage" :pages="videoPages" scroll-target=".media" />
      </div>
      </transition>

    </div>

    <Lightbox
      :open="lb.open"
      :items="lb.images"
      :index="lb.index"
      @update:index="lb.index = $event"
      @close="lb.open = false"
    />

    <VideoModal
      :open="vp.open"
      :src="vp.src"
      :poster="vp.poster"
      :title="vp.title"
      @close="vp.open = false"
    />
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { assetUrl, resolveImage, youtubeThumb } from '@/store/api'
import AppPagination from '@/components/common/AppPagination.vue'
import Lightbox from '@/components/common/Lightbox.vue'
import VideoModal from '@/components/common/VideoModal.vue'
import ViewCount from '@/components/common/ViewCount.vue'

// How many of the latest gallery photos this home-page preview shows: a 3-card
// row + a 4-card row.
const MAX_PHOTOS = 7
// Videolavhalar tab: one 4×2 grid per page, everything else behind the pager.
const VIDEOS_PER_PAGE = 8

export default {
  name: 'MediaPart',
  components: { AppPagination, Lightbox, VideoModal, ViewCount },
  data() {
    return {
      tab: 'foto',
      videoPage: 1,
      lb: { open: false, index: 0, images: [] }, // images = the photos shown here
      vp: { open: false, src: '', poster: '', title: '' }
    }
  },
  computed: {
    ...mapState('videos', { videos: state => state.items }),
    ...mapState('gallery', { galleryItems: state => state.items }),
    // Backend gallery images mapped into the original 3-big + 4-small (7-tile)
    // magazine shape. If the gallery has fewer than 7 photos we cycle through
    // them so the second row still fills its 4 tiles instead of collapsing.
    photos() {
      const src = this.galleryItems
      if (!src.length) return []
      return Array.from({ length: MAX_PHOTOS }, (_, idx) => {
        const g = src[idx % src.length]
        return { idx, id: g.id, title: g.title, src: resolveImage(g.image), views: g.views || 0 }
      })
    },
    rowOne() { return this.photos.slice(0, 3) },
    rowTwo() { return this.photos.slice(3, MAX_PHOTOS) },
    videoPages() { return Math.max(1, Math.ceil(this.videos.length / VIDEOS_PER_PAGE)) },
    pagedVideos() {
      const start = (this.videoPage - 1) * VIDEOS_PER_PAGE
      return this.videos.slice(start, start + VIDEOS_PER_PAGE)
    },
    // The lightbox slides through every photo shown in this section.
    photoSrcs() { return this.photos.map(p => p.src) }
  },
  watch: {
    // The list arrives after the first render (and can shrink) — keep the pager
    // pointing at a page that still exists.
    videoPages(pages) {
      if (this.videoPage > pages) this.videoPage = pages
    }
  },
  created() {
    this.fetchVideos()
    this.fetchGallery()
  },
  methods: {
    ...mapActions('videos', { fetchVideos: 'fetch', viewVideo: 'incrementView' }),
    ...mapActions('gallery', { fetchGallery: 'fetch', viewGallery: 'incrementView' }),
    src(path) {
      return assetUrl(path)
    },
    // Card thumbnail: admin poster if set, else the YouTube thumbnail for a
    // YouTube link, else '' → the template falls back to the clip's first frame.
    posterFor(item) {
      if (item.poster) return this.src(item.poster)
      return youtubeThumb(item.url)
    },
    openVideo(item) {
      this.vp = { open: true, src: this.src(item.url), poster: this.src(item.poster), title: item.title }
      this.viewVideo(item.id)
    },
    openPhoto(item) {
      // Open the lightbox at the clicked photo and count it as a gallery view.
      this.lb.images = this.photoSrcs
      this.lb.index = item.idx
      this.lb.open = true
      this.viewGallery(item.id)
    }
  }
}
</script>

<style lang="scss">
@import '../../styles/components/04media/media.scss';
</style>
